import EventEmitter from 'eventemitter3';
import { identity } from 'lodash';
import { proto as listenerProt } from '../proto/ListenerProto';
import { proto } from '../proto/AsrProto';
import { captureException } from '../tracker/raven';
import { Protobuf } from '../errors/ProtobufErrors';
import { wsLogger } from '../utils/logger/loggerFactory';

const types = {
    MAIN: 'main',
    AUDIO_INFO: 'audioInfo',
    AUDIO: 'audio',
    BROADCAST: 'broadcast',
    ASR: 'asr'
};

const wsLog = wsLogger;

interface WsParams {
    protocol?: string;
    host: string;
    port?: number;
    type?: string;
    encodeRequest?: (payload: unknown) => unknown;
    meta?: Record<string, unknown>;
}

export default class WsConnection extends EventEmitter {
    private _encodeRequest: (payload: unknown) => unknown;
    private _socket!: WebSocket;
    private _isClosingRequested: boolean;
    port?: number;
    type?: string;
    url: string;
    meta: Record<string, unknown>;

    constructor({ protocol = 'ws', host, port, type, encodeRequest = identity, meta }: WsParams) {
        super();
        this._encodeRequest = encodeRequest;
        this.port = port;
        this.type = type;
        this.url = `${protocol}://${host}${port ? `:${port}` : ''}`;
        this.meta = meta || {};
        this._isClosingRequested = false;
        try {
            this._socket = new WebSocket(this.url);
            this._socket.binaryType = 'arraybuffer';
            this._socket.onopen = () => this.emit('opened');
            this._socket.onclose = (event: CloseEvent) => {
                const { wasClean, code, reason } = event;
                // TODO: check behaviour on connection fail
                if (wasClean) {
                    wsLog.log(`Websocket ${this.url} was closed gracefully. Code: ${code};`, `Reason: ${reason || '—'};`, this.meta);
                } else {
                    wsLog.log(`Websocket ${this.url} closed abruptly, error code:${code};`, `Reason: ${reason || '—'};`, this.meta);
                }
                this.emit('closed', event);
            };
            this._socket.onmessage = (message: MessageEvent) => {
                try {
                    this.emit('message', new Uint8Array(message.data as ArrayBuffer));
                } catch (error) {
                    captureException(error);
                    wsLog.error((error as Error).message, error);
                    this._socket.close();
                }
            };
        } catch (error) {
            captureException(error);
            wsLog.log((error as Error).message, error);
        }
    }

    send(payload: unknown) {
        if (!this.isOpen()) {
            return;
        }
        const encoded = this._encodeRequest(payload);
        return this._socket.send(encoded as Parameters<WebSocket['send']>[0]);
    }

    getBufferedAmount() {
        return this._socket.bufferedAmount;
    }

    close(...args: [number?, string?]) {
        if (this.isClosed() || this.isClosing() || this._isClosingRequested) {
            return;
        }
        this._socket.close(...args);
    }

    isOpen() {
        return this._socket.readyState === this._socket.OPEN;
    }

    isConnecting() {
        return this._socket.readyState === this._socket.CONNECTING;
    }

    isClosing() {
        return this._socket.readyState === this._socket.CLOSING;
    }

    isClosed() {
        return this._socket.readyState === this._socket.CLOSED;
    }

    removeAllListeners(...args: [string?]) {
        return super.removeAllListeners(...(args as []));
    }
}

function encodeDaemonRequest(payload: unknown) {
    const description = listenerProt.Request.verify(payload);

    if (description) {
        const error = new Protobuf.ListenerRequestError({ description, payload });
        captureException(error);
        return wsLog.error(error.message, error);
    }
    const request = listenerProt.Request.create(payload);
    return listenerProt.Request.encode(request).finish();
}

function encodeAsrRequest(payload: unknown) {
    const description = proto.RecognizeRequest.verify(payload);

    if (description) {
        const error = new Protobuf.VibeRecognizeRequestError({ description, payload });
        captureException(error);
        return wsLog.error(error.message, error);
    }
    const request = proto.RecognizeRequest.create(payload);
    return proto.RecognizeRequest.encode(request).finish();
}

export function createAudioInfoConnection({ host, port }: { host: string; port?: number }) {
    return new WsConnection({
        host,
        port,
        type: types.AUDIO_INFO,
        encodeRequest: encodeDaemonRequest,
        meta: {
            description: 'Audio Info listener connection'
        }
    });
}

export function createAudioConnection({ host, port }: { host: string; port?: number }) {
    return new WsConnection({
        host,
        port,
        type: types.AUDIO,
        encodeRequest: encodeDaemonRequest,
        meta: {
            description: 'Mixed device listener connection'
        }
    });
}

export function createAsrWsConnection({
    protocol,
    host,
    port,
    meta
}: {
    protocol?: string;
    host: string;
    port?: number;
    meta?: Record<string, unknown>;
}) {
    return new WsConnection({
        protocol,
        host,
        port,
        type: types.ASR,
        encodeRequest: encodeAsrRequest,
        meta
    });
}

export function createEventForwardingConnection({ host, port }: { host: string; port?: number }) {
    return new WsConnection({
        host,
        port,
        type: types.BROADCAST,
        meta: {
            description: 'Event forwarding listener connection'
        }
    });
}
